import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Input } from '../../components/common/Input';
import Modal from '../../components/common/Modal';
import { Pagination } from '../../components/common/Pagination';
import { MagnifyingGlassIcon, ChevronDownIcon, PencilIcon, CheckIcon, XMarkIcon, ArrowDownTrayIcon, BellIcon, TruckIcon, TrashIcon } from '@heroicons/react/24/outline';
import { API_CONFIG } from '../../config/api';
import api from '../../services/api';
import ordersService from '../../services/ordersService';
import transportService from '../../services/transportService';
import pimService from '../../services/pimService';
import type { OrderFilters, OrderRecord, OrderStatus } from '../../types/orders';
import type { Product } from '../../types/pim';
import type { AlertRule, AlertResult } from '../../types/orderAlerts';
import { DEFAULT_ALERT_RULES, ALERT_COLOR_CONFIG } from '../../types/orderAlerts';
import { evaluateAllRules } from '../../utils/alertEngine';
import { AlertConfigurator } from '../../components/alerts/AlertConfigurator';
import toast from 'react-hot-toast';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const statusMeta: Record<OrderStatus, { label: string; variant: any }> = {
  new: { label: 'Nowe', variant: 'default' },
  drafting: { label: 'Rysunki', variant: 'gray' },
  awaiting_approval: { label: 'Do akceptacji', variant: 'warning' },
  in_production: { label: 'Produkcja', variant: 'info' },
  ready_for_collection: { label: 'Gotowe', variant: 'primary' },
  delivered: { label: 'Dostarczone', variant: 'success' },
  transport_ready: { label: 'Zamówienie gotowe do transportu', variant: 'info' },
  in_transport: { label: 'Przygotowano zlecenie', variant: 'primary' },
  in_transit: { label: 'W transporcie', variant: 'warning' },
};

const deriveStatus = (o: OrderRecord): OrderStatus => {
  // Priorytet 1: Status 'delivered' jest najważniejszy (końcowy stan)
  if (o.status === 'delivered') {
    return 'delivered';
  }

  // Priorytet 2: Status 'in_transport' z bazy danych (towar fizycznie w drodze)
  if (o.status === 'in_transport') {
    return 'in_transit';
  }

  // Priorytet 3: Sprawdź flagi transportowe
  if (o.has_transport && o.has_ordered) {
    return 'in_transport'; // zamówienie jest w aktywnym zleceniu (przygotowano zlecenie)
  }
  if (o.has_transport && !o.has_ordered) {
    return 'transport_ready'; // zamówienie przekazane do zleceń, ale nie w zleceniu
  }

  // Priorytet 4: Inne statusy z bazy danych
  if (o.status) return o.status;

  // Domyślnie nowe zamówienie
  return 'new';
};

export const Orders = () => {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<OrderFilters>({ page: 1, limit: 25, sort_by: 'order_date', sort_direction: 'DESC' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});
  const [editing, setEditing] = useState<Record<number, Partial<OrderRecord>>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editOrder, setEditOrder] = useState<Partial<OrderRecord>>({});
  // Transport modal
  const [transportOpen, setTransportOpen] = useState(false);
  const [transportOrderId, setTransportOrderId] = useState<number | null>(null);
  const [carriers, setCarriers] = useState<Array<{ id: number; name: string; email?: string }>>([]);
  const [transportForm, setTransportForm] = useState<any>({
    readiness_date: '',
    delivery_address: '',
    client_name: '',
    client_email: '',
    producer_logistics_email: '',
    producer_finance_email: '',
    notes: '',
    carrier_ids: [] as number[],
    packages: [{ length_mm: '', width_mm: '', height_mm: '', weight_kg: '' }]
  });
  const [newOrder, setNewOrder] = useState<Partial<OrderRecord>>({
    order_nr: '',
    order_date: '',
    ref_name: '',
    responsible: '',
    client_po: '',
    product_short_description: '',
    drawings_planned_date: '',
    drawing_approval_date: '',
    in_production_since: '',
    requested_delivery_week: '',
    proposed_lead_time_collection: '',
    confirmed_collection_date: '',
    delivery_mode: '',
    estimated_delivery_date: '',
    remarks_on_montage: '',
    delivery_list_no: '',
    delivery_address: '',
    onsite_contact_person: '',
    special_requirements: '',
    real_delivery_date: '',
  });

  // Product search state
  const [productSelectModalOpen, setProductSelectModalOpen] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [productListPage, setProductListPage] = useState(1);
  const [productListTotal, setProductListTotal] = useState(0);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const [useCustomProduct, setUseCustomProduct] = useState(false);
  const [customProductName, setCustomProductName] = useState('');
  const [customProductDescription, setCustomProductDescription] = useState('');
  const [productDetailOpen, setProductDetailOpen] = useState(false);
  const [viewingProduct, setViewingProduct] = useState<Product | null>(null);

  // Edit modal product selection state
  const [editSelectedProducts, setEditSelectedProducts] = useState<Product[]>([]);
  const [editProductSelectModalOpen, setEditProductSelectModalOpen] = useState(false);
  const [editProductSearch, setEditProductSearch] = useState('');
  const [editUseCustomProduct, setEditUseCustomProduct] = useState(false);
  const [editCustomProductName, setEditCustomProductName] = useState('');
  const [editCustomProductDescription, setEditCustomProductDescription] = useState('');

  // Alert system state
  const [alertRules, setAlertRules] = useState<AlertRule[]>([]);
  const [alertConfigOpen, setAlertConfigOpen] = useState(false);
  const [showLegend, setShowLegend] = useState(true);

  // Load alert rules from localStorage or use defaults
  useEffect(() => {
    const savedRules = localStorage.getItem('orderAlertRules');
    if (savedRules) {
      try {
        setAlertRules(JSON.parse(savedRules));
      } catch (e) {
        console.error('Failed to parse alert rules from localStorage', e);
        setAlertRules(DEFAULT_ALERT_RULES);
      }
    } else {
      setAlertRules(DEFAULT_ALERT_RULES);
    }
  }, []);

  // Save alert rules to localStorage whenever they change
  useEffect(() => {
    if (alertRules.length > 0) {
      localStorage.setItem('orderAlertRules', JSON.stringify(alertRules));
    }
  }, [alertRules]);

  // Compute alerts for all orders
  const orderAlerts = useMemo(() => {
    const alertsMap: Record<number, AlertResult | null> = {};
    orders.forEach(order => {
      alertsMap[order.id] = evaluateAllRules(order, alertRules);
    });
    return alertsMap;
  }, [orders, alertRules]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await ordersService.getOrders(filters);
      const withStatus = (res.data || []).map(o => ({ ...o, status: deriveStatus(o) }));
      setOrders(withStatus);
      setTotal(res.pagination.total);
      setTotalPages(res.pagination.totalPages);
    } catch (e: any) {
      setError(e?.response?.data?.message || 'Nie udało się pobrać zamówień');
    } finally {
      setLoading(false);
    }
  };

  const toggleTransport = async (orderId: number) => {
    const o = orders.find(x => x.id === orderId);

    if (!o) {
      toast.error('Nie znaleziono zamówienia');
      return;
    }

    // Jeśli zamówienie jest już w zleceniu transportowym (has_ordered=1), nie pozwól na zmianę
    if (o.has_ordered) {
      toast.error('Nie można zmienić - zamówienie jest już w zleceniu transportowym. Najpierw usuń zlecenie transportowe.');
      return;
    }

    try {
      // Toggle has_transport
      const response = await api.put(`/orders/${orderId}/toggle-transport`);

      // Odśwież listę zamówień
      await fetchOrders();
      toast.success(response.data.message);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || 'Nie udało się zmienić statusu zamówienia');
    }
  };

  const [stats, setStats] = useState<Record<string, number>>({ new: 0, drafting: 0, awaiting_approval: 0, in_production: 0, ready_for_collection: 0, delivered: 0, transport_ready: 0, prepared_transport: 0, in_transit: 0 });
  const [statsFiltered, setStatsFiltered] = useState(false);
  const fetchStats = async () => {
    try { setStats(await ordersService.getStats(statsFiltered ? filters : {})); } catch { /* ignore */ }
  };

  // Fallback alert calculator (in case backend alerts are missing due to cache/restart)
  const isBeforeToday = (d?: string | null) => {
    if (!d) return false;
    const ds = String(d).slice(0, 10);
    const today = new Date().toISOString().slice(0, 10);
    return ds < today;
  };
  const getAlerts = (o: OrderRecord): string[] => {
    const serverAlerts = o.alerts || [];
    if (serverAlerts.length > 0) return serverAlerts;
    const hasCollection = !!o.confirmed_collection_date;
    const overdueCollection = hasCollection && isBeforeToday(o.confirmed_collection_date);
    const missingDelivery = !o.estimated_delivery_date || !o.real_delivery_date;
    return overdueCollection && missingDelivery ? ['post_collection_missing_delivery'] : [];
  };

  // Stage change dialog state
  const todayIso = () => new Date().toISOString().slice(0,10);
  const [stageDialog, setStageDialog] = useState<{ open: boolean; orderId: number | null; stage: OrderStatus | null; date: string }>(
    { open: false, orderId: null, stage: null, date: todayIso() }
  );

  useEffect(() => {
    fetchOrders();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page, filters.limit, filters.sort_by, filters.sort_direction, statsFiltered]);

  useEffect(() => {
    // reset to first page on filter changes other than pagination/sort
    setFilters(prev => ({ ...prev, page: 1 }));
    // trigger fetch
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search, filters.responsible, filters.status, filters.date_from, filters.date_to, filters.delivery_from, filters.delivery_to]);

  // Fetch products for modal (with debounce for search)
  const fetchProductsForModal = async (page: number = 1, search: string = '') => {
    setLoadingProducts(true);
    try {
      const response = await pimService.getProducts({
        search: search.trim(),
        // Nie filtruj po statusie - pokazuj wszystkie produkty (draft i published)
        page,
        limit: 20,
        sort_by: 'name',
        sort_direction: 'asc' // lowercase - will be converted to uppercase in pimService
      });
      setProducts(response.data || []);
      setProductListTotal(response.pagination?.total || 0);
      setProductListPage(page);
    } catch (error) {
      console.error('❌ Error fetching products:', error);
      toast.error('Nie udało się pobrać listy produktów');
      setProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  // Load products when modal opens
  useEffect(() => {
    if (productSelectModalOpen) {
      fetchProductsForModal(1, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSelectModalOpen]);

  // Debounced search for products
  useEffect(() => {
    if (!productSelectModalOpen) return;
    if (!productSearch) return; // Don't search if empty

    const timeoutId = setTimeout(() => {
      fetchProductsForModal(1, productSearch);
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSearch, productSelectModalOpen]);

  // Edit modal - product search
  useEffect(() => {
    if (editProductSelectModalOpen) {
      fetchProductsForModal(1, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProductSelectModalOpen]);

  useEffect(() => {
    if (!editProductSelectModalOpen) return;
    if (!editProductSearch) return;

    const timeoutId = setTimeout(() => {
      fetchProductsForModal(1, editProductSearch);
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProductSearch, editProductSelectModalOpen]);

  const handleFilterChange = (key: keyof OrderFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({ page: 1, limit: 25, sort_by: 'order_date', sort_direction: 'DESC' });
  };

  const openAdd = () => {
    setNewOrder({ order_nr: '', order_date: '', ref_name: '', responsible: '', client_po: '', product_short_description: '' });
    setAddOpen(true);
  };

  const createOrder = async () => {
    if (!newOrder.order_nr || !String(newOrder.order_nr).trim()) {
      toast.error('Podaj numer zamówienia');
      return;
    }

    // Sprawdź czy są wybrane produkty
    if (selectedProducts.length === 0) {
      toast.error('Proszę wybrać produkty z listy lub dodać customowy produkt');
      return;
    }

    // Skompiluj wszystkie produkty (zwykłe + customowe)
    const productDescription = selectedProducts
      .map(p => {
        if (p.sku === 'CUSTOM') {
          // Customowy produkt: Nazwa + Opis
          return `${p.name}${p.description ? ` (${p.description})` : ''}`;
        } else {
          // Zwykły produkt: SKU - Nazwa
          return `${p.sku} - ${p.name}`;
        }
      })
      .join(', ');

    try {
      setAdding(true);
      await ordersService.createOrder({
        order_nr: String(newOrder.order_nr).trim(),
        order_date: newOrder.order_date || null,
        ref_name: newOrder.ref_name || null,
        responsible: newOrder.responsible || null,
        client_po: newOrder.client_po || null,
        product_short_description: productDescription,
        drawings_planned_date: newOrder.drawings_planned_date || null,
        drawing_approval_date: newOrder.drawing_approval_date || null,
        in_production_since: newOrder.in_production_since || null,
        requested_delivery_week: newOrder.requested_delivery_week || null,
        proposed_lead_time_collection: newOrder.proposed_lead_time_collection || null,
        confirmed_collection_date: newOrder.confirmed_collection_date || null,
        delivery_mode: (newOrder.delivery_mode as any) || null,
        estimated_delivery_date: newOrder.estimated_delivery_date || null,
        remarks_on_montage: newOrder.remarks_on_montage || null,
        delivery_list_no: newOrder.delivery_list_no || null,
        delivery_address: newOrder.delivery_address || null,
        onsite_contact_person: newOrder.onsite_contact_person || null,
        special_requirements: newOrder.special_requirements || null,
        real_delivery_date: newOrder.real_delivery_date || null,
      });

      // Reset product-related state
      setSelectedProducts([]);
      setProductSearch('');
      setProducts([]);
      setUseCustomProduct(false);
      setCustomProductName('');
      setCustomProductDescription('');

      setAddOpen(false);
      await fetchOrders();
      toast.success('Zamówienie zostało dodane');
    } catch (e) {
      toast.error('Nie udało się dodać zamówienia');
    } finally {
      setAdding(false);
    }
  };

  const quickSet = async (id: number, action: 'approve_drawings' | 'start_production' | 'confirm_collection' | 'mark_delivered') => {
    try {
      const updated = await ordersService.transition(id, action);
      setOrders(prev => prev.map(o => o.id === id ? { ...o, ...updated, status: deriveStatus(updated) } : o));
      fetchStats();
    } catch (e) { /* noop */ }
  };

  const startEdit = (id: number) => {
    const o = orders.find(x => x.id === id);
    if (!o) return;
    setEditingId(id);
    setEditOrder({
      order_nr: o.order_nr || '',
      client_po: o.client_po || '',
      order_date: o.order_date || '',
      ref_name: o.ref_name || '',
      responsible: o.responsible || '',
      product_short_description: o.product_short_description || '',
      drawings_planned_date: o.drawings_planned_date || '',
      drawing_approval_date: o.drawing_approval_date || '',
      in_production_since: o.in_production_since || '',
      requested_delivery_week: o.requested_delivery_week || '',
      proposed_lead_time_collection: o.proposed_lead_time_collection || '',
      confirmed_collection_date: o.confirmed_collection_date || '',
      delivery_mode: (o.delivery_mode as any) || '',
      estimated_delivery_date: o.estimated_delivery_date || '',
      remarks_on_montage: o.remarks_on_montage || '',
      delivery_list_no: o.delivery_list_no || '',
      delivery_address: o.delivery_address || '',
      onsite_contact_person: o.onsite_contact_person || '',
      special_requirements: o.special_requirements || '',
      real_delivery_date: o.real_delivery_date || '',
    });

    // Parse product_short_description into editSelectedProducts
    const description = o.product_short_description || '';
    if (description) {
      const parsedItems = description.split(',').map(item => item.trim());

      // Pobierz nazwy produktów z opisów (po " - ")
      const productNames = parsedItems
        .filter(item => item.includes(' - '))
        .map(item => item.split(' - ')[1].trim());

      // Załaduj prawdziwe produkty z bazy po nazwie
      if (productNames.length > 0) {
        pimService.getProducts({ search: '', page: 1, limit: 1000 })
          .then(response => {
            const allProducts = response.data || [];
            const matchedProducts = allProducts.filter(p => productNames.includes(p.name));

            // Dodaj też custom produkty (bez " - ")
            const customProducts = parsedItems
              .filter(item => !item.includes(' - '))
              .map((product, idx) => ({
                id: -idx - 1,
                sku: 'CUSTOM',
                name: product,
                description: product
              }));

            setEditSelectedProducts([...matchedProducts, ...customProducts as Product[]]);
          })
          .catch(err => {
            console.error('Error loading products:', err);
            // Fallback do starych fikcyjnych produktów
            const parsedProducts = parsedItems.map((product, idx) => {
              if (product.includes(' - ')) {
                const [sku, name] = product.split(' - ').map(s => s.trim());
                return { id: -idx - 1, sku, name, description: '' };
              } else {
                return { id: -idx - 1, sku: 'CUSTOM', name: product, description: product };
              }
            });
            setEditSelectedProducts(parsedProducts as Product[]);
          });
      } else {
        // Tylko custom produkty
        const customProducts = parsedItems.map((product, idx) => ({
          id: -idx - 1,
          sku: 'CUSTOM',
          name: product,
          description: product
        }));
        setEditSelectedProducts(customProducts as Product[]);
      }
    } else {
      setEditSelectedProducts([]);
    }

    setEditOpen(true);
  };

  const cancelEdit = (id: number) => {
    setEditing(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const submitEdit = async () => {
    if (!editingId) return;

    // Compile products back to product_short_description
    const productDescription = editSelectedProducts
      .map(p => {
        if (p.sku === 'CUSTOM') {
          return `${p.name}${p.description && p.description !== p.name ? ` (${p.description})` : ''}`;
        } else {
          return `${p.sku} - ${p.name}`;
        }
      })
      .join(', ');

    try {
      const updated = await ordersService.updateOrder(editingId, {
        ...editOrder,
        product_short_description: productDescription
      });
      setOrders(prev => prev.map(o => o.id === editingId ? { ...o, ...updated, status: deriveStatus(updated) } : o));
      setEditOpen(false);
      setEditingId(null);
      setEditSelectedProducts([]);
      setEditProductSearch('');
      setEditUseCustomProduct(false);
      setEditCustomProductName('');
      setEditCustomProductDescription('');
      fetchStats();
    } catch (e) {
      toast.error('Nie udało się zapisać zmian');
    }
  };

  const deleteOrder = async (id: number, orderNr: string) => {
    if (!confirm(`Czy na pewno chcesz usunąć zamówienie ${orderNr}?\n\nTa operacja jest nieodwracalna!`)) {
      return;
    }

    try {
      await ordersService.deleteOrder(id);
      setOrders(prev => prev.filter(o => o.id !== id));
      toast.success(`Zamówienie ${orderNr} zostało usunięte`);
      fetchStats();
    } catch (e) {
      toast.error('Nie udało się usunąć zamówienia');
    }
  };

  const defaultColumns = ['order_nr','order_date','client_po','ref_name','responsible','product_short_description','requested_delivery_week','confirmed_collection_date','delivery_mode','estimated_delivery_date','real_delivery_date','has_transport'];
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem('orders.visibleColumns');
    if (saved) {
      const parsed = JSON.parse(saved);
      // Add new columns that are in defaultColumns but not in saved
      const merged = [...new Set([...parsed, ...defaultColumns.filter(col => !parsed.includes(col))])];
      return merged;
    }
    return defaultColumns;
  });
  useEffect(() => { localStorage.setItem('orders.visibleColumns', JSON.stringify(visibleColumns)); }, [visibleColumns]);
  const allColumns = defaultColumns;

  // Sortowanie lokalne
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Zmień kierunek sortowania
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Nowa kolumna - domyślnie rosnąco
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Posortowane zamówienia
  const sortedOrders = useMemo(() => {
    if (!sortColumn) return orders;

    return [...orders].sort((a, b) => {
      let aVal: any = a[sortColumn as keyof OrderRecord];
      let bVal: any = b[sortColumn as keyof OrderRecord];

      // Konwersja do porównywalnych wartości
      if (sortColumn === 'order_nr') {
        // Numer zamówienia - porównaj numerycznie jeśli możliwe
        const aNum = parseInt(String(aVal || '0'));
        const bNum = parseInt(String(bVal || '0'));
        if (!isNaN(aNum) && !isNaN(bNum)) {
          return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
        }
      }

      if (sortColumn === 'order_date' || sortColumn === 'confirmed_collection_date' || sortColumn === 'estimated_delivery_date' || sortColumn === 'real_delivery_date') {
        // Daty - porównaj jako stringi (format YYYY-MM-DD)
        aVal = String(aVal || '');
        bVal = String(bVal || '');
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      // Tekst - porównaj alfabetycznie
      aVal = String(aVal || '').toLowerCase();
      bVal = String(bVal || '').toLowerCase();
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [orders, sortColumn, sortDirection]);

  return (
    <div>
      <ColumnPickerStyles />
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Zamówienia</CardTitle>
            <div className="flex items-center gap-2">
              <div className="relative">
                <ColumnPicker allColumns={allColumns} selected={visibleColumns} onChange={setVisibleColumns} />
              </div>
              <Button variant="outline" onClick={async () => {
                try {
                  const url = `${API_CONFIG.baseURL}${ordersService.buildExportUrl(filters)}`;
                  const token = localStorage.getItem('access_token');

                  const response = await fetch(url, {
                    headers: {
                      'Authorization': `Bearer ${token}`
                    }
                  });

                  if (!response.ok) {
                    throw new Error('Export failed');
                  }

                  const blob = await response.blob();
                  const downloadUrl = window.URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = downloadUrl;
                  a.download = `orders_export_${new Date().toISOString().slice(0,10)}.csv`;
                  document.body.appendChild(a);
                  a.click();
                  document.body.removeChild(a);
                  window.URL.revokeObjectURL(downloadUrl);

                  toast.success('Plik CSV został pobrany');
                } catch (e) {
                  toast.error('Nie udało się pobrać pliku CSV');
                }
              }}>
                <ArrowDownTrayIcon className="h-5 w-5 mr-2" /> Eksport CSV
              </Button>
              <Button
                onClick={() => setAlertConfigOpen(true)}
                className="bg-red-600 hover:bg-red-700 text-white border-red-600"
              >
                Alerty
              </Button>
              <Button onClick={openAdd}>Dodaj zamówienie</Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Statystyki</div>
            <label className="flex items-center gap-2 text-xs text-gray-600">
              <input type="checkbox" className="h-4 w-4 rounded" checked={statsFiltered} onChange={(e) => setStatsFiltered(e.target.checked)} />
              Wg filtrów
            </label>
          </div>
          {/* Bar Chart Statistics */}
          <div className="bg-white rounded-lg border shadow-sm p-4 mb-4 outline-none focus:outline-none">
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={[
                  { name: 'Nowe', value: stats.new || 0, color: '#10b981' },
                  { name: 'Rysunki', value: stats.drafting || 0, color: '#6b7280' },
                  { name: 'Do akceptacji', value: stats.awaiting_approval || 0, color: '#eab308' },
                  { name: 'Produkcja', value: stats.in_production || 0, color: '#3b82f6' },
                  { name: 'Gotowe', value: stats.ready_for_collection || 0, color: '#a855f7' },
                  { name: 'Dostarczone', value: stats.delivered || 0, color: '#22c55e' },
                  { name: 'Gotowe do transportu', value: stats.transport_ready || 0, color: '#06b6d4' },
                  { name: 'Przygotowano zlecenie', value: stats.prepared_transport || 0, color: '#2563eb' },
                  { name: 'W transporcie', value: stats.in_transit || 0, color: '#f97316' },
                ]}
                margin={{ top: 45, right: 5, left: 5, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis hide />
                <YAxis
                  hide
                  domain={[0, (dataMax: number) => Math.max(dataMax * 1.2, 1)]}
                />
                <Tooltip
                  cursor={false}
                  wrapperStyle={{ display: 'none' }}
                />
                <Bar
                  dataKey="value"
                  radius={[8, 8, 0, 0]}
                  isAnimationActive={true}
                  animationDuration={800}
                  animationEasing="linear"
                  minPointSize={20}
                  label={(props: any) => {
                    const { x, y, width, value } = props;
                    return (
                      <text
                        x={x + width / 2}
                        y={y - 10}
                        fill="#1f2937"
                        textAnchor="middle"
                        fontSize={22}
                        fontWeight={700}
                        style={{
                          filter: 'drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.1))',
                          fontFamily: 'system-ui, -apple-system, sans-serif'
                        }}
                      >
                        {value}
                      </text>
                    );
                  }}
                >
                  {[
                    { name: 'Nowe', value: stats.new || 0, color: '#10b981' },
                    { name: 'Rysunki', value: stats.drafting || 0, color: '#6b7280' },
                    { name: 'Do akceptacji', value: stats.awaiting_approval || 0, color: '#eab308' },
                    { name: 'Produkcja', value: stats.in_production || 0, color: '#3b82f6' },
                    { name: 'Gotowe', value: stats.ready_for_collection || 0, color: '#a855f7' },
                    { name: 'Dostarczone', value: stats.delivered || 0, color: '#22c55e' },
                    { name: 'Gotowe do transportu', value: stats.transport_ready || 0, color: '#06b6d4' },
                    { name: 'Przygotowano zlecenie', value: stats.prepared_transport || 0, color: '#2563eb' },
                    { name: 'W transporcie', value: stats.in_transit || 0, color: '#f97316' },
                  ].map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>

            {/* Legenda statusów */}
            <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
              <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#10b981' }}></div>
                <span className="text-xs font-medium text-gray-700">Nowe</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-lg">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#6b7280' }}></div>
                <span className="text-xs font-medium text-gray-700">Rysunki</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#eab308' }}></div>
                <span className="text-xs font-medium text-gray-700">Do akceptacji</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#3b82f6' }}></div>
                <span className="text-xs font-medium text-gray-700">Produkcja</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-lg">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#a855f7' }}></div>
                <span className="text-xs font-medium text-gray-700">Gotowe</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#22c55e' }}></div>
                <span className="text-xs font-medium text-gray-700">Dostarczone</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-50 border border-cyan-200 rounded-lg">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#06b6d4' }}></div>
                <span className="text-xs font-medium text-gray-700">Gotowe do transportu</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#2563eb' }}></div>
                <span className="text-xs font-medium text-gray-700">Przygotowano zlecenie</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 border border-orange-200 rounded-lg">
                <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: '#f97316' }}></div>
                <span className="text-xs font-medium text-gray-700">W transporcie</span>
              </div>
            </div>
          </div>

          {/* Alert Legend - Kafelki */}
          {showLegend && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-xs text-gray-500 uppercase tracking-wide font-semibold">Legenda alertów</div>
                <button
                  onClick={() => setShowLegend(false)}
                  className="text-xs text-gray-500 hover:text-gray-700 underline transition-colors"
                >
                  Ukryj
                </button>
              </div>
              <div className="flex flex-wrap gap-3">
                {Object.entries(ALERT_COLOR_CONFIG).map(([color, config]) => {
                  const gradients: Record<string, string> = {
                    red: 'from-red-50 via-red-100 to-red-200 border-red-400',
                    orange: 'from-orange-50 via-orange-100 to-orange-200 border-orange-400',
                    yellow: 'from-yellow-50 via-yellow-100 to-amber-100 border-yellow-400',
                    green: 'from-green-50 via-green-100 to-emerald-100 border-green-400',
                    blue: 'from-blue-50 via-blue-100 to-indigo-100 border-blue-400'
                  };
                  const iconColors: Record<string, string> = {
                    red: 'text-red-600',
                    orange: 'text-orange-600',
                    yellow: 'text-yellow-600',
                    green: 'text-green-600',
                    blue: 'text-blue-600'
                  };
                  return (
                    <div
                      key={color}
                      className={`group bg-gradient-to-br ${gradients[color]} border-2 rounded-xl px-5 py-3 shadow-md hover:shadow-xl transition-all duration-300 cursor-default hover:-translate-y-0.5`}
                    >
                      <div className="flex items-center gap-2">
                        <svg className={`w-4 h-4 ${iconColors[color]}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                        </svg>
                        <div className="text-xs font-bold text-gray-800 uppercase tracking-wide">{config.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!showLegend && (
            <div className="mb-4 text-center">
              <button
                onClick={() => setShowLegend(true)}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Pokaż legendę alertów
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div className="col-span-2">
              <label className="block text-xs text-gray-500 mb-1">Szukaj</label>
              <div className="relative">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Nr zam., PO, ref"
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                />
              </div>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Handlowiec</label>
              <input
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                placeholder="Imię i nazwisko"
                value={filters.responsible || ''}
                onChange={(e) => handleFilterChange('responsible', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                value={filters.status || ''}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Wszystkie statusy</option>
                <option value="new">Nowe</option>
                <option value="drafting">Rysunki</option>
                <option value="awaiting_approval">Do akceptacji</option>
                <option value="in_production">Produkcja</option>
                <option value="ready_for_collection">Gotowe</option>
                <option value="delivered">Dostarczone</option>
                <option value="transport_ready">Zamówienie gotowe do transportu</option>
                <option value="in_transport">Przygotowano zlecenie</option>
                <option value="in_transit">W transporcie</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Data zamówienia od</label>
              <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={filters.date_from || ''} onChange={(e) => handleFilterChange('date_from', e.target.value)} />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Data zamówienia do</label>
              <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500" value={filters.date_to || ''} onChange={(e) => handleFilterChange('date_to', e.target.value)} />
            </div>
          </div>
          <div className="flex justify-end mt-3">
            <Button variant="outline" onClick={clearFilters}>Wyczyść filtry</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Lista zamówień ({total})</CardTitle>
            <div className="text-sm text-gray-500">Strona {filters.page} / {totalPages}</div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center items-center py-10 text-gray-600">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mr-3"></div>
              Ładowanie…
            </div>
          ) : error ? (
            <div className="text-center py-10">
              <div className="text-red-600 mb-4">{error}</div>
              <Button variant="outline" onClick={fetchOrders}>Spróbuj ponownie</Button>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-14 text-gray-600">
              Brak zamówień. Dodaj pierwsze lub zaimportuj.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-gray-50 via-gray-100 to-gray-50">
                  <tr>
                    <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                    {visibleColumns.map(col => {
                      const isSortable = ['order_nr', 'order_date', 'client_po', 'ref_name', 'responsible', 'confirmed_collection_date', 'estimated_delivery_date', 'real_delivery_date'].includes(col);
                      const isActive = sortColumn === col;

                      return (
                        <th
                          key={col}
                          className={`px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider ${isSortable ? 'cursor-pointer hover:bg-gray-200 transition-colors select-none' : ''}`}
                          onClick={() => isSortable && handleSort(col)}
                        >
                          <div className="flex items-center gap-2">
                            <span>
                              {col === 'alerts' && 'Alerty'}
                              {col === 'order_nr' && 'Nr zamówienia'}
                              {col === 'order_date' && 'Data zamówienia'}
                              {col === 'client_po' && 'Nr PO klienta'}
                              {col === 'ref_name' && 'Nazwa ref.'}
                              {col === 'responsible' && 'Handlowiec'}
                              {col === 'product_short_description' && 'Opis produktu'}
                              {col === 'requested_delivery_week' && 'Tydzień dostawy'}
                              {col === 'confirmed_collection_date' && 'Termin załadunku'}
                              {col === 'delivery_mode' && 'Rodzaj dostawy'}
                              {col === 'estimated_delivery_date' && 'Szac. dostawa'}
                              {col === 'real_delivery_date' && 'Realna dostawa'}
                              {col === 'has_transport' && 'Przekazano do transportu'}
                            </span>
                            {isSortable && (
                              <span className="flex flex-col">
                                <svg className={`w-3 h-3 ${isActive && sortDirection === 'asc' ? 'text-blue-600' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M5.293 9.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 7.414V15a1 1 0 11-2 0V7.414L6.707 9.707a1 1 0 01-1.414 0z" />
                                </svg>
                                <svg className={`w-3 h-3 -mt-1 ${isActive && sortDirection === 'desc' ? 'text-blue-600' : 'text-gray-400'}`} fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M14.707 10.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 12.586V5a1 1 0 012 0v7.586l2.293-2.293a1 1 0 011.414 0z" />
                                </svg>
                              </span>
                            )}
                          </div>
                        </th>
                      );
                    })}
                    <th className="px-4 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Akcje</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedOrders.map((o) => {
                    const isExpanded = !!expanded[o.id];
                    const isEditing = !!editing[o.id];
                    const st = o.status || deriveStatus(o);
                    const alert = orderAlerts[o.id];
                    const alertConfig = alert ? ALERT_COLOR_CONFIG[alert.color] : null;
                    const isNewOrder = st === 'new';
                    const isDelivered = st === 'delivered';

                    // Priorytet: delivered > new > alerty
                    let rowClassName = 'hover:bg-gray-50';
                    if (isDelivered) {
                      rowClassName += ' delivered-order-green';
                    } else if (isNewOrder) {
                      rowClassName += ' new-order-blink';
                    } else if (alert && alertConfig) {
                      rowClassName += ` ${alertConfig.bg} ${alertConfig.border}`;
                    }

                    return (
                      <>
                        <tr key={o.id} className={rowClassName}>
                          <td className="px-4 py-3 whitespace-nowrap min-w-[190px]">
                            <div className="flex items-center gap-2">
                              {/* 1. Status select - jako pierwsze */}
                              {(() => {
                                // Statusy automatyczne (ustawiane przez moduł transportowy) - nie można ich zmienić ręcznie
                                const isAutomaticStatus = st === 'delivered' || st === 'transport_ready' || st === 'in_transport' || st === 'in_transit';
                                const disabledTitle =
                                  st === 'delivered'
                                    ? 'Status "Dostarczone" jest ustawiany automatycznie przez moduł transportowy i nie może być zmieniony ręcznie'
                                    : st === 'transport_ready'
                                    ? 'Status "Gotowe do transportu" jest ustawiany automatycznie gdy zamówienie jest w gotowych do transportu'
                                    : st === 'in_transport'
                                    ? 'Status "Przygotowano zlecenie" jest ustawiany automatycznie gdy zamówienie jest w aktywnym zleceniu transportowym'
                                    : st === 'in_transit'
                                    ? 'Status "W transporcie" jest ustawiany automatycznie gdy zlecenie transportowe jest w trakcie dostawy'
                                    : '';

                                return (
                                  <select
                                    className={`px-2 py-1 border rounded-md text-sm ${isAutomaticStatus ? 'bg-gray-100 cursor-not-allowed opacity-70' : ''}`}
                                    value={st}
                                    disabled={isAutomaticStatus}
                                    title={isAutomaticStatus ? disabledTitle : 'Kliknij aby zmienić status'}
                                    onChange={async (e) => {
                                      const next = e.target.value as OrderStatus;

                                      // Blokada zmiany na status "new"
                                      if (next === 'new' && st !== 'new') {
                                        toast.error('Nie można zmienić statusu zamówienia na "Nowe". Status "Nowe" jest dostępny tylko dla nowo utworzonych zamówień.');
                                        e.target.value = st; // Reset dropdown do poprzedniej wartości
                                        return;
                                      }

                                      // Blokada zmiany na statusy transportowe (te są automatyczne)
                                      if ((next === 'transport_ready' || next === 'in_transport' || next === 'in_transit') && st !== next) {
                                        toast.error('Nie można ręcznie ustawić statusu transportowego. Status jest ustawiany automatycznie przez moduł transportowy.');
                                        e.target.value = st;
                                        return;
                                      }

                                      // Open date dialog, default today
                                      setStageDialog({ open: true, orderId: o.id, stage: next, date: todayIso() });
                                    }}
                                  >
                                    <option value="new">Nowe</option>
                                    <option value="drafting">Rysunki</option>
                                    <option value="awaiting_approval">Do akceptacji</option>
                                    <option value="in_production">Produkcja</option>
                                    <option value="ready_for_collection">Gotowe</option>
                                    <option value="delivered" disabled={st !== 'delivered'} title={st !== 'delivered' ? 'Status automatyczny - ustawiany przez moduł transportowy' : ''}>
                                      Dostarczone {st !== 'delivered' && '(automatyczny)'}
                                    </option>
                                    <option value="transport_ready" disabled={st !== 'transport_ready'} title={st !== 'transport_ready' ? 'Status automatyczny - ustawiany przez moduł transportowy' : ''}>
                                      Zamówienie gotowe do transportu {st !== 'transport_ready' && '(automatyczny)'}
                                    </option>
                                    <option value="in_transport" disabled={st !== 'in_transport'} title={st !== 'in_transport' ? 'Status automatyczny - ustawiany przez moduł transportowy' : ''}>
                                      Przygotowano zlecenie {st !== 'in_transport' && '(automatyczny)'}
                                    </option>
                                    <option value="in_transit" disabled={st !== 'in_transit'} title={st !== 'in_transit' ? 'Status automatyczny - ustawiany przez moduł transportowy' : ''}>
                                      W transporcie {st !== 'in_transit' && '(automatyczny)'}
                                    </option>
                                  </select>
                                );
                              })()}
                              {/* 2. Alerty - w środku */}
                              {alert && (
                                <button
                                  onClick={(e) => {
                                    e.preventDefault();
                                    toast(
                                      <div>
                                        <div className="font-semibold mb-1">{alertConfig?.icon} {alert.ruleName}</div>
                                        <div className="text-sm text-gray-600">Priorytet: {alert.priority}</div>
                                        <div className="text-sm text-gray-600">Typ: {alertConfig?.label}</div>
                                      </div>,
                                      {
                                        duration: 5000,
                                        style: { maxWidth: '400px' }
                                      }
                                    );
                                  }}
                                  className={`p-1.5 rounded-full ${alertConfig?.bg} border hover:shadow-md transition-shadow cursor-pointer`}
                                  title={alert.ruleName}
                                >
                                  <BellIcon className={`h-5 w-5 ${alertConfig?.text}`} />
                                </button>
                              )}
                              {/* 3. Ikona ciężarówki - na końcu (po prawej) */}
                              {(st === 'transport_ready' || st === 'in_transport') && (
                                <div className={`p-1.5 rounded-full ${st === 'in_transport' ? 'bg-blue-100 border border-blue-400' : 'bg-cyan-100 border border-cyan-400'}`}>
                                  <TruckIcon className={`h-5 w-5 ${st === 'in_transport' ? 'text-blue-600' : 'text-cyan-600'}`} />
                                </div>
                              )}
                            </div>
                          </td>
                          {visibleColumns.map((col) => (
                            <td key={col} className={`px-4 py-3 text-sm text-gray-900 ${col === 'order_nr' ? 'font-medium' : ''} ${col === 'product_short_description' ? 'max-w-[280px] truncate' : ''}`}>
                              {col === 'alerts' && (
                                alert ? (
                                  <span className={`inline-flex items-center text-xs ${alertConfig?.text} border rounded px-2 py-0.5`} title={alert.ruleName}>
                                    {alert.ruleName}
                                  </span>
                                ) : <span className="text-xs text-gray-400">—</span>
                              )}
                              {col === 'order_nr' && o.order_nr}
                              {col === 'order_date' && (o.order_date || '-')}
                              {col === 'client_po' && (o.client_po || '-')}
                              {col === 'ref_name' && (o.ref_name || '-')}
                              {col === 'responsible' && (o.responsible || '-')}
                              {col === 'product_short_description' && (o.product_short_description || '-')}
                              {col === 'requested_delivery_week' && (o.requested_delivery_week || '-')}
                              {col === 'confirmed_collection_date' && (
                                isEditing ? (
                                  <input type="date" className="border rounded px-2 py-1" value={(editing[o.id]?.confirmed_collection_date as string) || ''} onChange={(e) => setEditing(prev => ({ ...prev, [o.id]: { ...prev[o.id], confirmed_collection_date: e.target.value }}))} />
                                ) : (
                                  o.confirmed_collection_date || '-'
                                )
                              )}
                              {col === 'delivery_mode' && (o.delivery_mode || '-')}
                              {col === 'estimated_delivery_date' && (
                                isEditing ? (
                                  <input type="date" className="border rounded px-2 py-1" value={(editing[o.id]?.estimated_delivery_date as string) || ''} onChange={(e) => setEditing(prev => ({ ...prev, [o.id]: { ...prev[o.id], estimated_delivery_date: e.target.value }}))} />
                                ) : (
                                  o.estimated_delivery_date || '-'
                                )
                              )}
                              {col === 'real_delivery_date' && (
                                isEditing ? (
                                  <input type="date" className="border rounded px-2 py-1" value={(editing[o.id]?.real_delivery_date as string) || ''} onChange={(e) => setEditing(prev => ({ ...prev, [o.id]: { ...prev[o.id], real_delivery_date: e.target.value }}))} />
                                ) : (
                                  o.real_delivery_date || '-'
                                )
                              )}
                              {col === 'has_transport' && (
                                <div className="flex items-center justify-center">
                                  {o.has_transport ? (
                                    <div className="flex items-center gap-2 text-green-600">
                                      <TruckIcon className="h-5 w-5" />
                                      <input
                                        type="checkbox"
                                        checked={true}
                                        disabled
                                        className="h-4 w-4 text-green-600 cursor-not-allowed"
                                        title="Przekazano do zleceń transportowych"
                                      />
                                    </div>
                                  ) : (
                                    <input
                                      type="checkbox"
                                      checked={false}
                                      disabled
                                      className="h-4 w-4 cursor-not-allowed"
                                      title="Nie przekazano do transportu"
                                    />
                                  )}
                                </div>
                              )}
                            </td>
                          ))}
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {!isEditing ? (
                              <div className="flex items-center gap-2 flex-wrap">
                                <Button size="sm" onClick={() => startEdit(o.id)} className="min-w-[160px] bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200">
                                  <PencilIcon className="h-4 w-4 mr-1" />
                                  Szczegóły i edycja
                                </Button>
                                {/* Dynamiczny przycisk transportowy */}
                                {(() => {
                                  const st = deriveStatus(o);
                                  const canMarkAsReadyForTransport = !o.has_transport && st === 'ready_for_collection';

                                  if (!o.has_transport) {
                                    return (
                                      <Button
                                        size="sm"
                                        onClick={() => toggleTransport(o.id)}
                                        disabled={!canMarkAsReadyForTransport}
                                        title={canMarkAsReadyForTransport ? "Przekaż do transportu" : "Nie możesz tego kliknąć dopóki zamówienie nie zmieni stanu na 'Gotowe'"}
                                        className={`min-w-[220px] ${canMarkAsReadyForTransport ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200' : 'bg-gray-400 text-white border-gray-400 cursor-not-allowed opacity-60'}`}
                                      >
                                        <TruckIcon className="h-4 w-4 mr-1" />
                                        Przekaż do transportu
                                      </Button>
                                    );
                                  }

                                  if (o.has_ordered) {
                                    return (
                                      <Button
                                        size="sm"
                                        disabled
                                        title="Nie można usunąć - zamówienie jest w zleceniu transportowym"
                                        className="min-w-[220px] bg-gray-400 text-white border-gray-400 cursor-not-allowed opacity-60"
                                      >
                                        <TruckIcon className="h-4 w-4 mr-1" />
                                        W zleceniu transportowym
                                      </Button>
                                    );
                                  }

                                  return (
                                    <Button
                                      size="sm"
                                      onClick={() => toggleTransport(o.id)}
                                      title="Usuń z gotowych do transportu"
                                      className="min-w-[220px] bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                                    >
                                      <XMarkIcon className="h-4 w-4 mr-1" />
                                      Usuń z gotowych do transportu
                                    </Button>
                                  );
                                })()}
                                <Button
                                  size="sm"
                                  onClick={() => deleteOrder(o.id, o.order_nr)}
                                  className="min-w-[160px] bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
                                >
                                  <TrashIcon className="h-4 w-4 mr-1" />
                                  Usuń zamówienie
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <Button size="sm" onClick={() => submitEdit()} className="min-w-[40px] bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.05] transition-all duration-200">
                                  <CheckIcon className="h-5 w-5" />
                                </Button>
                                <Button size="sm" onClick={() => cancelEdit(o.id)} className="min-w-[40px] bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white shadow-md hover:shadow-lg transform hover:scale-[1.05] transition-all duration-200">
                                  <XMarkIcon className="h-5 w-5" />
                                </Button>
                              </div>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className={rowClassName}>
                            <td colSpan={visibleColumns.length + 2} className="bg-gray-50 px-4 py-4">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                <div className="md:col-span-3 bg-white border rounded p-3 flex flex-wrap items-center gap-4">
                                  <div>
                                    <div className="text-gray-500 text-xs">Załadunek</div>
                                    <div className="font-medium">{o.confirmed_collection_date || '—'}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500 text-xs">Szac. dostawa</div>
                                    <div className="font-medium">{o.estimated_delivery_date || '—'}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500 text-xs">Dostarczono</div>
                                    <div className="font-medium">{o.real_delivery_date || '—'}</div>
                                  </div>
                                  {(o.alerts && o.alerts.length > 0) && (
                                    <div className="ml-auto text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1" title={o.alerts.map(a => alertLabels[a] || a).join(', ')}>
                                      Alerty: {o.alerts.length}
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div className="text-gray-500">Plan rysunków</div>
                                  <div>
                                    {editing[o.id] ? (
                                      <input type="date" className="border rounded px-2 py-1" value={(editing[o.id]?.drawings_planned_date as string) || ''} onChange={(e) => setEditing(prev => ({ ...prev, [o.id]: { ...prev[o.id], drawings_planned_date: e.target.value }}))} />
                                    ) : (
                                      o.drawings_planned_date || '-'
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-500">Akceptacja rysunków</div>
                                  <div>
                                    {editing[o.id] ? (
                                      <input type="date" className="border rounded px-2 py-1" value={(editing[o.id]?.drawing_approval_date as string) || ''} onChange={(e) => setEditing(prev => ({ ...prev, [o.id]: { ...prev[o.id], drawing_approval_date: e.target.value }}))} />
                                    ) : (
                                      o.drawing_approval_date || '-'
                                    )}
                                  </div>
                                </div>
                                <div>
                                  <div className="text-gray-500">Produkcja od</div>
                                  <div>
                                    {editing[o.id] ? (
                                      <input type="date" className="border rounded px-2 py-1" value={(editing[o.id]?.in_production_since as string) || ''} onChange={(e) => setEditing(prev => ({ ...prev, [o.id]: { ...prev[o.id], in_production_since: e.target.value }}))} />
                                    ) : (
                                      o.in_production_since || '-'
                                    )}
                                  </div>
                                </div>
                                <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-4">
                                  <div>
                                    <div className="text-gray-500">Uwagi dot. montażu</div>
                                    <div className="text-gray-900">{o.remarks_on_montage || '-'}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500">Nr listu przewozowego</div>
                                    <div className="text-gray-900">{o.delivery_list_no || '-'}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500">Rodzaj dostawy</div>
                                    <div className="text-gray-900">{o.delivery_mode || '-'}</div>
                                  </div>
                                  <div className="md:col-span-2">
                                    <div className="text-gray-500">Adres dostawy</div>
                                    <div className="text-gray-900">{o.delivery_address || '-'}</div>
                                  </div>
                                  <div>
                                    <div className="text-gray-500">Osoba kontaktowa</div>
                                    <div className="text-gray-900">{o.onsite_contact_person || '-'}</div>
                                  </div>
                                  <div className="md:col-span-3">
                                    <div className="text-gray-500">Wymogi specjalne</div>
                                    <div className="text-gray-900">{o.special_requirements || '-'}</div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-4">
            <Pagination
              currentPage={filters.page || 1}
              totalPages={totalPages}
              onPageChange={(p) => setFilters(prev => ({ ...prev, page: p }))}
            />
          </div>
        </CardContent>
      </Card>

      {/* Transport Modal */}
      <Modal isOpen={transportOpen} onClose={() => setTransportOpen(false)} title={t('transport.modal.title','Dodaj transport do zamówienia')} size="xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('transport.fields.readiness','Data gotowości')}</label>
            <input type="date" className="w-full border rounded px-3 py-2" value={transportForm.readiness_date} onChange={(e) => setTransportForm((p:any) => ({ ...p, readiness_date: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('transport.fields.clientEmail','E-mail klienta')}</label>
            <input type="email" className="w-full border rounded px-3 py-2" value={transportForm.client_email} onChange={(e) => setTransportForm((p:any) => ({ ...p, client_email: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('transport.fields.deliveryAddress','Adres dostawy')}</label>
            <textarea className="w-full border rounded px-3 py-2" rows={2} value={transportForm.delivery_address} onChange={(e) => setTransportForm((p:any) => ({ ...p, delivery_address: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('transport.fields.producerLogistics','Logistyka (producent)')}</label>
            <input type="email" className="w-full border rounded px-3 py-2" value={transportForm.producer_logistics_email} onChange={(e) => setTransportForm((p:any) => ({ ...p, producer_logistics_email: e.target.value }))} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('transport.fields.producerFinance','Księgowość (producent)')}</label>
            <input type="email" className="w-full border rounded px-3 py-2" value={transportForm.producer_finance_email} onChange={(e) => setTransportForm((p:any) => ({ ...p, producer_finance_email: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('transport.fields.carriers','Lista Przewoźników')}</label>
            <div className="flex flex-wrap gap-2">
              {carriers.map(c => (
                <label key={c.id} className="text-sm border rounded px-2 py-1 flex items-center gap-2">
                  <input type="checkbox" checked={transportForm.carrier_ids.includes(c.id)} onChange={(e) => {
                    setTransportForm((p:any) => {
                      const set = new Set<number>(p.carrier_ids);
                      e.target.checked ? set.add(c.id) : set.delete(c.id);
                      return { ...p, carrier_ids: Array.from(set) };
                    });
                  }} />
                  {c.name}
                </label>
              ))}
              {carriers.length === 0 && <div className="text-xs text-gray-500">Brak zdefiniowanych przewoźników</div>}
            </div>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">{t('transport.fields.packages','Wymiar ładowny')}</label>
            <div className="space-y-2">
              {transportForm.packages.map((p:any, idx:number) => (
                <div key={idx} className="grid grid-cols-4 gap-2">
                  <input placeholder="Długość [mm]" className="border rounded px-2 py-1" value={p.length_mm} onChange={(e) => setTransportForm((pf:any) => { const arr=[...pf.packages]; arr[idx]={...arr[idx], length_mm: e.target.value}; return { ...pf, packages: arr }; })} />
                  <input placeholder="Szerokość [mm]" className="border rounded px-2 py-1" value={p.width_mm} onChange={(e) => setTransportForm((pf:any) => { const arr=[...pf.packages]; arr[idx]={...arr[idx], width_mm: e.target.value}; return { ...pf, packages: arr }; })} />
                  <input placeholder="Wysokość [mm]" className="border rounded px-2 py-1" value={p.height_mm} onChange={(e) => setTransportForm((pf:any) => { const arr=[...pf.packages]; arr[idx]={...arr[idx], height_mm: e.target.value}; return { ...pf, packages: arr }; })} />
                  <input placeholder="Waga [kg]" className="border rounded px-2 py-1" value={p.weight_kg} onChange={(e) => setTransportForm((pf:any) => { const arr=[...pf.packages]; arr[idx]={...arr[idx], weight_kg: e.target.value}; return { ...pf, packages: arr }; })} />
                </div>
              ))}
              <Button variant="outline" onClick={() => setTransportForm((pf:any) => ({ ...pf, packages: [...pf.packages, { length_mm:'', width_mm:'', height_mm:'', weight_kg:'' }] }))}>{t('transport.actions.addPackage','Dodaj paczkę')}</Button>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => setTransportOpen(false)}>{t('common.cancel','Anuluj')}</Button>
          <Button onClick={async () => {
            if (!transportOrderId) return;
            try {
              const payload = {
                order_ids: [transportOrderId],
                carrier_ids: transportForm.carrier_ids,
                readiness_date: transportForm.readiness_date || undefined,
                delivery_address: transportForm.delivery_address || undefined,
                client_name: transportForm.client_name || undefined,
                client_email: transportForm.client_email || undefined,
                producer_logistics_email: transportForm.producer_logistics_email || undefined,
                producer_finance_email: transportForm.producer_finance_email || undefined,
                notes: transportForm.notes || undefined,
                packages: transportForm.packages.map((p:any) => ({
                  length_mm: Number(p.length_mm) || undefined,
                  width_mm: Number(p.width_mm) || undefined,
                  height_mm: Number(p.height_mm) || undefined,
                  weight_kg: Number(p.weight_kg) || undefined,
                }))
              };
              await transportService.create(payload);
              toast.success('Zlecenie transportowe zostało utworzone');
              setTransportOpen(false);
              fetchOrders(); // Refresh orders list to update has_transport flag
            } catch (e) {
              toast.error(t('transport.messages.createError','Nie udało się utworzyć zlecenia transportowego'));
            }
          }}>{t('common.save','Zapisz')}</Button>
        </div>
      </Modal>
      {/* Add Order Modal */}
      <Modal
        isOpen={addOpen}
        onClose={() => {
          // Reset product-related state
          setSelectedProducts([]);
          setProductSearch('');
          setProducts([]);
          setUseCustomProduct(false);
          setCustomProductName('');
          setCustomProductDescription('');
          setAddOpen(false);
        }}
        title="Nowe zamówienie"
        size="xl"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Nr zamówienia" value={newOrder.order_nr as any} onChange={(e) => setNewOrder(prev => ({ ...prev, order_nr: e.target.value }))} placeholder="Wpisz numer zamówienia" />
          <Input label="Nr PO klienta" value={newOrder.client_po as any} onChange={(e) => setNewOrder(prev => ({ ...prev, client_po: e.target.value }))} placeholder="PO / zamówienie klienta" />
          <Input label="Data zamówienia" type="date" value={newOrder.order_date as any} onChange={(e) => setNewOrder(prev => ({ ...prev, order_date: e.target.value }))} />
          <Input label="Nazwa ref." value={newOrder.ref_name as any} onChange={(e) => setNewOrder(prev => ({ ...prev, ref_name: e.target.value }))} placeholder="Referencja" />
          <Input label="Handlowiec" value={newOrder.responsible as any} onChange={(e) => setNewOrder(prev => ({ ...prev, responsible: e.target.value }))} placeholder="Osoba odpowiedzialna" />

          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          {/* SEKCJA PRODUKTÓW */}
          {/* ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━ */}
          <div className="md:col-span-2 border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Produkty
            </label>

            {/* Input do otwierania modalu wyboru produktów */}
            <div className="relative">
              <input
                type="text"
                readOnly
                className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                placeholder="Kliknij aby wybrać produkty z listy..."
                value={selectedProducts.length > 0 ? `Wybrano: ${selectedProducts.length} produktów` : ''}
                onClick={() => setProductSelectModalOpen(true)}
              />
              <div className="absolute right-3 top-3 text-gray-400 pointer-events-none">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Wybrane produkty - KAFELKI */}
            {selectedProducts.length > 0 && (
              <div className="mt-4">
                <div className="text-sm text-gray-600 mb-2">
                  Wybrane produkty ({selectedProducts.length}):
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedProducts.map(product => {
                    const isCustom = product.sku === 'CUSTOM';
                    return (
                      <div
                        key={product.id}
                        className={`relative rounded-lg p-3 group min-w-[200px] ${
                          isCustom
                            ? 'bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-300'
                            : 'bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-300'
                        }`}
                      >
                        {/* Przycisk usuń - pokazuje się na hover */}
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedProducts(selectedProducts.filter(p => p.id !== product.id));
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          ✕
                        </button>

                        {/* Badge customowy/zwykły */}
                        {isCustom && (
                          <div className="absolute -top-2 -left-2 bg-purple-600 text-white rounded-full px-2 py-0.5 text-xs font-bold">
                            CUSTOM
                          </div>
                        )}

                        <div className="text-xs text-gray-500 uppercase mb-1">Nazwa:</div>
                        <div className="text-sm text-gray-700 font-medium mb-2">
                          {product.name}
                        </div>

                        {isCustom ? (
                          <>
                            <div className="text-xs text-gray-500 uppercase mb-1">Opis:</div>
                            <div className="text-xs text-gray-700 line-clamp-2">
                              {product.description || 'Brak opisu'}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="text-xs text-gray-500 uppercase mb-1">SKU:</div>
                            <div className="font-medium text-sm text-blue-900">{product.sku}</div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Checkbox: Customowy produkt */}
            <div className="mt-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={useCustomProduct}
                  onChange={(e) => setUseCustomProduct(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700 font-medium">
                  Dodaj customowy produkt razem z opisem
                </span>
              </label>
            </div>

            {/* Formularz customowego produktu - TYLKO gdy checkbox zaznaczony */}
            {useCustomProduct && (
              <div className="mt-3 p-4 border border-gray-300 rounded-lg bg-gray-50">
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nazwa produktu
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Wpisz nazwę customowego produktu..."
                      value={customProductName}
                      onChange={(e) => setCustomProductName(e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Krótki opis
                    </label>
                    <textarea
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows={3}
                      placeholder="Wpisz krótki opis produktu..."
                      value={customProductDescription}
                      onChange={(e) => setCustomProductDescription(e.target.value)}
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={() => {
                        if (!customProductName.trim()) {
                          toast.error('Podaj nazwę customowego produktu');
                          return;
                        }

                        // Utwórz customowy produkt jako obiekt Product z negatywnym ID
                        const customProduct: Product = {
                          id: -(Date.now()), // Negatywny ID aby odróżnić od produktów z bazy
                          sku: 'CUSTOM',
                          name: customProductName.trim(),
                          slug: '',
                          status: 'draft',
                          description: customProductDescription.trim(),
                          product_type_id: null,
                          created_at: new Date().toISOString(),
                          updated_at: new Date().toISOString()
                        };

                        // Dodaj do wybranych produktów
                        setSelectedProducts([...selectedProducts, customProduct]);

                        // Wyczyść pola
                        setCustomProductName('');
                        setCustomProductDescription('');
                        setUseCustomProduct(false);

                        toast.success('Customowy produkt został dodany');
                      }}
                    >
                      Dodaj customowy produkt
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          <Input label="Plan rysunków" type="date" value={newOrder.drawings_planned_date as any} onChange={(e) => setNewOrder(prev => ({ ...prev, drawings_planned_date: e.target.value }))} />
          <Input label="Akceptacja rysunków" type="date" value={newOrder.drawing_approval_date as any} onChange={(e) => setNewOrder(prev => ({ ...prev, drawing_approval_date: e.target.value }))} />
          <Input label="Produkcja od" type="date" value={newOrder.in_production_since as any} onChange={(e) => setNewOrder(prev => ({ ...prev, in_production_since: e.target.value }))} />
          <Input label="Tydzień dostawy" value={newOrder.requested_delivery_week as any} onChange={(e) => setNewOrder(prev => ({ ...prev, requested_delivery_week: e.target.value }))} placeholder="np. 2025-W42" />
          <Input label="Proponowany termin załadunku" type="date" value={newOrder.proposed_lead_time_collection as any} onChange={(e) => setNewOrder(prev => ({ ...prev, proposed_lead_time_collection: e.target.value }))} />
          <Input label="Potwierdzony termin załadunku" type="date" value={newOrder.confirmed_collection_date as any} onChange={(e) => setNewOrder(prev => ({ ...prev, confirmed_collection_date: e.target.value }))} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rodzaj dostawy</label>
            <select className="w-full border rounded-md px-3 py-2" value={newOrder.delivery_mode as any} onChange={(e) => setNewOrder(prev => ({ ...prev, delivery_mode: e.target.value }))}>
              <option value="">— wybierz —</option>
              <option value="collection">Odbiór własny</option>
              <option value="delivery">Dostawa</option>
              <option value="pickup">Pickup</option>
            </select>
          </div>
          <Input label="Szacunkowa data dostawy" type="date" value={newOrder.estimated_delivery_date as any} onChange={(e) => setNewOrder(prev => ({ ...prev, estimated_delivery_date: e.target.value }))} />
          <Input label="Nr listu przewozowego" value={newOrder.delivery_list_no as any} onChange={(e) => setNewOrder(prev => ({ ...prev, delivery_list_no: e.target.value }))} />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Adres dostawy</label>
            <textarea className="w-full border rounded-md px-3 py-2" rows={2} value={newOrder.delivery_address as any} onChange={(e) => setNewOrder(prev => ({ ...prev, delivery_address: e.target.value }))} />
          </div>
          <Input label="Osoba kontaktowa" value={newOrder.onsite_contact_person as any} onChange={(e) => setNewOrder(prev => ({ ...prev, onsite_contact_person: e.target.value }))} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data dostawy (realna)</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={newOrder.real_delivery_date as any}
              onChange={(e) => setNewOrder(prev => ({ ...prev, real_delivery_date: e.target.value }))}
            />
            <p className="text-xs text-gray-500 mt-1">Uzupełnij jeśli jest znana</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Uwagi montaż</label>
            <textarea className="w-full border rounded-md px-3 py-2" rows={2} value={newOrder.remarks_on_montage as any} onChange={(e) => setNewOrder(prev => ({ ...prev, remarks_on_montage: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Wymogi specjalne</label>
            <textarea className="w-full border rounded-md px-3 py-2" rows={2} value={newOrder.special_requirements as any} onChange={(e) => setNewOrder(prev => ({ ...prev, special_requirements: e.target.value }))} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => {
              // Reset product-related state
              setSelectedProducts([]);
              setProductSearch('');
              setProducts([]);
              setUseCustomProduct(false);
              setCustomProductName('');
              setCustomProductDescription('');
              setAddOpen(false);
            }}
          >
            Anuluj
          </Button>
          <Button onClick={createOrder} disabled={adding}>{adding ? 'Zapisywanie…' : 'Zapisz'}</Button>
        </div>
      </Modal>

      {/* Product Selection Modal */}
      <Modal
        isOpen={productSelectModalOpen}
        onClose={() => {
          setProductSelectModalOpen(false);
          setProductSearch('');
        }}
        title="Wybierz produkty"
        size="xl"
      >
        <div className="space-y-4">
          {/* Search produktów w modalu */}
          <div className="relative">
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Szukaj po SKU lub nazwie..."
              value={productSearch}
              onChange={(e) => setProductSearch(e.target.value)}
              autoFocus
            />
            {loadingProducts && (
              <div className="absolute right-3 top-3">
                <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
              </div>
            )}
          </div>

          {/* Tabela z produktami */}
          {loadingProducts && products.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-spin inline-block h-8 w-8 border-2 border-blue-500 rounded-full border-t-transparent mb-2"></div>
              <p>Ładowanie produktów...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nie znaleziono produktów</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12"></th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nazwa</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {products.map(product => {
                      const isSelected = selectedProducts.find(sp => sp.id === product.id);
                      return (
                        <tr key={product.id} className={isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={!!isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedProducts([...selectedProducts, product]);
                                } else {
                                  setSelectedProducts(selectedProducts.filter(sp => sp.id !== product.id));
                                }
                              }}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{product.name}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">{product.sku}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Paginacja */}
          {productListTotal > 20 && (
            <div className="flex items-center justify-between pt-3 border-t">
              <div className="text-sm text-gray-600">
                Strona {productListPage} z {Math.ceil(productListTotal / 20)} (łącznie: {productListTotal} produktów)
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => fetchProductsForModal(productListPage - 1, productSearch)}
                  disabled={productListPage === 1}
                  className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  ← Poprzednia
                </button>
                <button
                  type="button"
                  onClick={() => fetchProductsForModal(productListPage + 1, productSearch)}
                  disabled={productListPage >= Math.ceil(productListTotal / 20)}
                  className="px-3 py-1 border rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Następna →
                </button>
              </div>
            </div>
          )}

          {/* Footer z przyciskami */}
          <div className="flex justify-between items-center pt-3 border-t">
            <div className="text-sm text-gray-600">
              {selectedProducts.length > 0 ? `Wybrano: ${selectedProducts.length} produktów` : 'Nie wybrano produktów'}
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setProductSelectModalOpen(false);
                  setProductSearch('');
                }}
              >
                Zamknij
              </Button>
              <Button
                onClick={() => {
                  setProductSelectModalOpen(false);
                  setProductSearch('');
                  toast.success(`Wybrano ${selectedProducts.length} produktów`);
                }}
              >
                Zatwierdź wybór ({selectedProducts.length})
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Product Details Modal */}
      <Modal
        isOpen={productDetailOpen}
        onClose={() => {
          setProductDetailOpen(false);
          setViewingProduct(null);
        }}
        title="Szczegóły produktu"
        size="lg"
      >
        {viewingProduct && (
          <div className="space-y-4">
            {/* Header z SKU i statusem */}
            <div className="flex items-start justify-between pb-3 border-b">
              <div>
                <h3 className="text-lg font-bold text-gray-900">{viewingProduct.name}</h3>
                <p className="text-sm text-gray-500 mt-1">SKU: {viewingProduct.sku}</p>
              </div>
              <div>
                {viewingProduct.status === 'published' ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Opublikowany
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    ⚠ Szkic
                  </span>
                )}
              </div>
            </div>

            {/* Opis produktu */}
            {viewingProduct.description && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Opis</h4>
                <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-700 whitespace-pre-wrap">
                  {viewingProduct.description}
                </div>
              </div>
            )}

            {/* Slug */}
            {viewingProduct.slug && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-1">Slug</h4>
                <p className="text-sm text-gray-600 font-mono">{viewingProduct.slug}</p>
              </div>
            )}

            {/* Typ produktu */}
            {viewingProduct.product_type_id && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-1">ID typu produktu</h4>
                <p className="text-sm text-gray-600">{viewingProduct.product_type_id}</p>
              </div>
            )}

            {/* Footer z przyciskiem zamknięcia */}
            <div className="flex justify-end pt-3 border-t">
              <Button
                variant="outline"
                onClick={() => {
                  setProductDetailOpen(false);
                  setViewingProduct(null);
                }}
              >
                Zamknij
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Stage Change Modal */}
      <Modal isOpen={stageDialog.open} onClose={() => setStageDialog(s => ({ ...s, open: false }))} title="Zmień etap" size="sm">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data etapu</label>
            <input type="date" className="w-full border rounded-md px-3 py-2" value={stageDialog.date} onChange={(e) => setStageDialog(s => ({ ...s, date: e.target.value }))} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setStageDialog(s => ({ ...s, open: false }))}>Anuluj</Button>
            <Button onClick={async () => {
              if (!stageDialog.orderId || !stageDialog.stage) { setStageDialog(s => ({ ...s, open: false })); return; }

              // Dodatkowa blokada na poziomie zapisu (edge case)
              const currentOrder = orders.find(x => x.id === stageDialog.orderId);
              if (stageDialog.stage === 'new' && currentOrder?.status !== 'new') {
                toast.error('Nie można zmienić statusu zamówienia na "Nowe". Status "Nowe" jest dostępny tylko dla nowo utworzonych zamówień.');
                setStageDialog(s => ({ ...s, open: false }));
                return;
              }

              try {
                const updated = await ordersService.setStage(stageDialog.orderId, stageDialog.stage, stageDialog.date);
                setOrders(prev => prev.map(x => x.id === stageDialog.orderId ? { ...x, ...updated, status: deriveStatus(updated) } : x));
                fetchStats();
              } finally {
                setStageDialog(s => ({ ...s, open: false }));
              }
            }}>Zapisz</Button>
          </div>
        </div>
      </Modal>

      {/* Edit Order Modal */}
      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} title="Edytuj zamówienie" size="xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input label="Nr zamówienia" value={editOrder.order_nr as any} onChange={(e) => setEditOrder(prev => ({ ...prev, order_nr: e.target.value }))} />
          <Input label="Nr PO klienta" value={editOrder.client_po as any} onChange={(e) => setEditOrder(prev => ({ ...prev, client_po: e.target.value }))} />
          <Input label="Data zamówienia" type="date" value={editOrder.order_date as any} onChange={(e) => setEditOrder(prev => ({ ...prev, order_date: e.target.value }))} />
          <Input label="Nazwa ref." value={editOrder.ref_name as any} onChange={(e) => setEditOrder(prev => ({ ...prev, ref_name: e.target.value }))} />
          <Input label="Handlowiec" value={editOrder.responsible as any} onChange={(e) => setEditOrder(prev => ({ ...prev, responsible: e.target.value }))} />

          {/* Produkty - Kafelki z możliwością edycji */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Produkty</label>

            {/* Input do otwierania modalu wyboru produktów */}
            <div className="relative mb-4">
              <input
                type="text"
                readOnly
                className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                placeholder="Kliknij aby wybrać produkty z listy..."
                value={editSelectedProducts.length > 0 ? `Wybrano: ${editSelectedProducts.length} produktów` : ''}
                onClick={() => setEditProductSelectModalOpen(true)}
              />
              <div className="absolute right-3 top-3 text-gray-400 pointer-events-none">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>

            {/* Wybrane produkty - KAFELKI z przyciskiem usuń */}
            {editSelectedProducts.length > 0 && (
              <div className="mt-4">
                <div className="text-sm text-gray-600 mb-2">
                  Wybrane produkty ({editSelectedProducts.length}):
                </div>
                <div className="flex flex-wrap gap-2">
                  {editSelectedProducts.map(product => {
                    const isCustom = product.sku === 'CUSTOM';
                    return (
                      <div
                        key={product.id}
                        className={`relative rounded-lg p-3 group min-w-[200px] ${
                          isCustom
                            ? 'bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-300'
                            : 'bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-300'
                        }`}
                      >
                        {/* Przycisk usuń */}
                        <button
                          type="button"
                          onClick={() => {
                            setEditSelectedProducts(editSelectedProducts.filter(p => p.id !== product.id));
                          }}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          ✕
                        </button>

                        {isCustom && (
                          <div className="absolute -top-2 -left-2 bg-purple-600 text-white rounded-full px-2 py-0.5 text-xs font-bold">
                            CUSTOM
                          </div>
                        )}

                        <div className="text-xs text-gray-500 uppercase mb-1">Nazwa:</div>
                        <div className="text-sm text-gray-700 font-medium mb-2">
                          {product.name}
                        </div>

                        {isCustom ? (
                          product.description && product.description !== product.name && (
                            <>
                              <div className="text-xs text-gray-500 uppercase mb-1">Opis:</div>
                              <div className="text-xs text-gray-700 line-clamp-2">
                                {product.description}
                              </div>
                            </>
                          )
                        ) : (
                          <>
                            <div className="text-xs text-gray-500 uppercase mb-1">SKU:</div>
                            <div className="font-medium text-sm text-blue-900">{product.sku}</div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Checkbox: Customowy produkt */}
            <div className="mt-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={editUseCustomProduct}
                  onChange={(e) => setEditUseCustomProduct(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                />
                <span className="text-gray-700">Dodaj customowy produkt (bez SKU)</span>
              </label>
            </div>

            {/* Pola customowego produktu */}
            {editUseCustomProduct && (
              <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-md space-y-2">
                <Input
                  label="Nazwa customowego produktu"
                  value={editCustomProductName}
                  onChange={(e) => setEditCustomProductName(e.target.value)}
                  placeholder="np. Wiata rowerowa custom"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Opis produktu</label>
                  <textarea
                    value={editCustomProductDescription}
                    onChange={(e) => setEditCustomProductDescription(e.target.value)}
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    rows={2}
                    placeholder="Opcjonalny opis..."
                  />
                </div>
                <Button
                  size="sm"
                  onClick={() => {
                    if (!editCustomProductName.trim()) {
                      toast.error('Wpisz nazwę produktu');
                      return;
                    }
                    const customProduct: Product = {
                      id: -Date.now(),
                      sku: 'CUSTOM',
                      name: editCustomProductName.trim(),
                      description: editCustomProductDescription.trim() || editCustomProductName.trim()
                    };
                    setEditSelectedProducts([...editSelectedProducts, customProduct]);
                    setEditCustomProductName('');
                    setEditCustomProductDescription('');
                    setEditUseCustomProduct(false);
                    toast.success('Customowy produkt dodany');
                  }}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  Dodaj customowy produkt
                </Button>
              </div>
            )}
          </div>

          <Input label="Plan rysunków" type="date" value={editOrder.drawings_planned_date as any} onChange={(e) => setEditOrder(prev => ({ ...prev, drawings_planned_date: e.target.value }))} />
          <Input label="Akceptacja rysunków" type="date" value={editOrder.drawing_approval_date as any} onChange={(e) => setEditOrder(prev => ({ ...prev, drawing_approval_date: e.target.value }))} />
          <Input label="Produkcja od" type="date" value={editOrder.in_production_since as any} onChange={(e) => setEditOrder(prev => ({ ...prev, in_production_since: e.target.value }))} />
          <Input label="Tydzień dostawy" value={editOrder.requested_delivery_week as any} onChange={(e) => setEditOrder(prev => ({ ...prev, requested_delivery_week: e.target.value }))} />
          <Input label="Proponowany termin załadunku" type="date" value={editOrder.proposed_lead_time_collection as any} onChange={(e) => setEditOrder(prev => ({ ...prev, proposed_lead_time_collection: e.target.value }))} />
          <Input label="Potwierdzony termin załadunku" type="date" value={editOrder.confirmed_collection_date as any} onChange={(e) => setEditOrder(prev => ({ ...prev, confirmed_collection_date: e.target.value }))} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rodzaj dostawy</label>
            <select className="w-full border rounded-md px-3 py-2" value={editOrder.delivery_mode as any} onChange={(e) => setEditOrder(prev => ({ ...prev, delivery_mode: e.target.value }))}>
              <option value="">— wybierz —</option>
              <option value="collection">Odbiór własny</option>
              <option value="delivery">Dostawa</option>
              <option value="pickup">Pickup</option>
            </select>
          </div>
          <Input label="Szacunkowa data dostawy" type="date" value={editOrder.estimated_delivery_date as any} onChange={(e) => setEditOrder(prev => ({ ...prev, estimated_delivery_date: e.target.value }))} />
          <Input label="Nr listu przewozowego" value={editOrder.delivery_list_no as any} onChange={(e) => setEditOrder(prev => ({ ...prev, delivery_list_no: e.target.value }))} />
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Adres dostawy</label>
            <textarea className="w-full border rounded-md px-3 py-2" rows={2} value={editOrder.delivery_address as any} onChange={(e) => setEditOrder(prev => ({ ...prev, delivery_address: e.target.value }))} />
          </div>
          <Input label="Osoba kontaktowa" value={editOrder.onsite_contact_person as any} onChange={(e) => setEditOrder(prev => ({ ...prev, onsite_contact_person: e.target.value }))} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Data dostawy (realna)</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={editOrder.real_delivery_date as any}
              onChange={(e) => setEditOrder(prev => ({ ...prev, real_delivery_date: e.target.value }))}
            />
            <p className="text-xs text-gray-500 mt-1">Uzupełnij jeśli jest znana</p>
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Uwagi montaż</label>
            <textarea className="w-full border rounded-md px-3 py-2" rows={2} value={editOrder.remarks_on_montage as any} onChange={(e) => setEditOrder(prev => ({ ...prev, remarks_on_montage: e.target.value }))} />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Wymogi specjalne</label>
            <textarea className="w-full border rounded-md px-3 py-2" rows={2} value={editOrder.special_requirements as any} onChange={(e) => setEditOrder(prev => ({ ...prev, special_requirements: e.target.value }))} />
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={() => setEditOpen(false)}>Anuluj</Button>
          <Button onClick={submitEdit}>Zapisz</Button>
        </div>
      </Modal>

      {/* Edit - Product Selection Modal */}
      <Modal
        isOpen={editProductSelectModalOpen}
        onClose={() => {
          setEditProductSelectModalOpen(false);
          setEditProductSearch('');
        }}
        title="Wybierz produkty"
        size="xl"
      >
        <div className="space-y-4">
          {/* Search produktów w modalu */}
          <div className="relative">
            <input
              type="text"
              className="w-full border border-gray-300 rounded-md px-3 py-2 pr-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Szukaj po SKU lub nazwie..."
              value={editProductSearch}
              onChange={(e) => setEditProductSearch(e.target.value)}
              autoFocus
            />
            {loadingProducts && (
              <div className="absolute right-3 top-3">
                <div className="animate-spin h-4 w-4 border-2 border-blue-500 rounded-full border-t-transparent"></div>
              </div>
            )}
          </div>

          {/* Tabela z produktami */}
          {loadingProducts && products.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <div className="animate-spin inline-block h-8 w-8 border-2 border-blue-500 rounded-full border-t-transparent mb-2"></div>
              <p>Ładowanie produktów...</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>Nie znaleziono produktów</p>
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <div className="max-h-96 overflow-y-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-12"></th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nazwa</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {products
                      .sort((a, b) => {
                        // Zaznaczone na górze
                        const aSelected = editSelectedProducts.find(sp => sp.name === a.name);
                        const bSelected = editSelectedProducts.find(sp => sp.name === b.name);
                        if (aSelected && !bSelected) return -1;
                        if (!aSelected && bSelected) return 1;
                        return 0;
                      })
                      .map(product => {
                        const isSelected = editSelectedProducts.find(sp => sp.name === product.name);
                        return (
                          <tr key={product.id} className={isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}>
                            <td className="px-4 py-3">
                              <input
                                type="checkbox"
                                checked={!!isSelected}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setEditSelectedProducts([...editSelectedProducts, product]);
                                  } else {
                                    setEditSelectedProducts(editSelectedProducts.filter(sp => sp.name !== product.name));
                                  }
                                }}
                                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                              />
                            </td>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{product.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{product.sku}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Przyciski */}
          <div className="flex justify-between items-center pt-4 border-t">
            <div className="text-sm text-gray-600">
              Zaznaczono: <span className="font-semibold">{editSelectedProducts.length}</span> {editSelectedProducts.length === 1 ? 'produkt' : 'produktów'}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => {
                setEditProductSelectModalOpen(false);
                setEditProductSearch('');
              }}>
                Zamknij
              </Button>
              <Button onClick={() => {
                setEditProductSelectModalOpen(false);
                setEditProductSearch('');
                toast.success(`Wybrano ${editSelectedProducts.length} ${editSelectedProducts.length === 1 ? 'produkt' : 'produktów'}`);
              }}>
                Zatwierdź
              </Button>
            </div>
          </div>
        </div>
      </Modal>

      {/* Alert Configurator Modal */}
      <AlertConfigurator
        isOpen={alertConfigOpen}
        onClose={() => setAlertConfigOpen(false)}
        rules={alertRules}
        onSave={(newRules) => {
          setAlertRules(newRules);
          toast.success('Reguły alertów zostały zapisane');
        }}
      />
    </div>
  );
};

export default Orders;

// Column Picker Component and styles
const columnsLabels: Record<string, string> = {
  order_nr: 'Nr zamówienia',
  order_date: 'Data zamówienia',
  client_po: 'Nr PO klienta',
  ref_name: 'Nazwa ref.',
  responsible: 'Handlowiec',
  product_short_description: 'Opis produktu',
  requested_delivery_week: 'Tydzień dostawy',
  confirmed_collection_date: 'Termin załadunku',
  delivery_mode: 'Rodzaj dostawy',
  estimated_delivery_date: 'Szac. dostawa',
  real_delivery_date: 'Realna dostawa',
  has_transport: 'Przekazano do transportu',
};

function ColumnPicker({ allColumns, selected, onChange }: { allColumns: string[]; selected: string[]; onChange: (c: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const toggle = (key: string) => {
    const set = new Set(selected);
    set.has(key) ? set.delete(key) : set.add(key);
    onChange(Array.from(set));
  };
  return (
    <div className="inline-block relative">
      <Button variant="outline" onClick={() => setOpen(!open)}>
        <ChevronDownIcon className="h-5 w-5 mr-2" /> Kolumny
      </Button>
      {open && (
        <div className="column-picker-pop">
          {allColumns.map(key => (
            <label key={key} className="flex items-center gap-2 py-1">
              <input type="checkbox" className="h-4 w-4" checked={selected.includes(key)} onChange={() => toggle(key)} />
              <span className="text-sm">{columnsLabels[key] || key}</span>
            </label>
          ))}
          <div className="flex gap-2 mt-2">
            <Button size="sm" variant="outline" onClick={() => onChange(allColumns)}>Wszystkie</Button>
            <Button size="sm" variant="outline" onClick={() => onChange(['order_nr','ref_name','responsible','confirmed_collection_date','estimated_delivery_date','real_delivery_date'])}>Minimalne</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ColumnPickerStyles() {
  return (
    <style>
      {`.column-picker-pop{position:absolute;z-index:20;margin-top:.5rem;right:0;min-width:220px;background:#fff;border:1px solid #e5e7eb;border-radius:.5rem;box-shadow:0 10px 15px -3px rgba(0,0,0,.1),0 4px 6px -2px rgba(0,0,0,.05);padding:.5rem 1rem}`}
      {`@keyframes new-order-pulse{0%,100%{background-color:rgba(16,185,129,0.25)}50%{background-color:rgba(16,185,129,0.5)}}`}
      {`.new-order-blink{animation:new-order-pulse 1s ease-in-out infinite}`}
      {`.delivered-order-green{background-color:rgba(34,197,94,0.15) !important;border-left:4px solid rgb(34,197,94) !important}`}
    </style>
  );
}

const alertLabels: Record<string, string> = {
  post_collection_missing_delivery: 'Po terminie załadunku: brak szac. dostawy lub dostawy realnej',
};
